// dependencies
import * as Sentry from "@sentry/react";
import React from "react";

// schema
import { AiSettingsSchema, type IAIAssistantsSettings } from "@performyard/models";

import { AdminContainer, AdminTitle } from "../../../../components/Admin";
//components
import Button from "../../../../components/Button";
import { Grid, GridCol } from "../../../../components/Grid";
import { Checkboxes } from "../../../../components/Inputs/SelectionOptions/Checkboxes";
import Toggle from "../../../../components/Inputs/ToggleSwitches/ThumbSwitch";
import PageTitle from "../../../../components/PageTitle";
import Spacer from "../../../../components/Spacer";
import Text from "../../../../components/Text";

// hooks
import { useAiSettings, useUpdateAiSettings } from "../../../../api/hooks";
import { getFeatureFlagQuery, useFeatureFlagQuery } from "../../../../hooks/useFeatureFlag";
import useForm from "../../../../hooks/useForm";
import useToast from "../../../../hooks/useToast";

import { companyAISettingsQuery, currentUserQuery } from "../../../../api/queries";
import { dataLoader } from "../../../../api/queries/dataLoader";

// utils
import { showError } from "../../../../utils/RequestReponse";

// pages
import ErrorPage from "../../../Error";
// styles
import css from "./AIReviewSummarization.module.scss";

/** Data loader function */
export const aIReviewSummarizationSettingsLoader = () => {
	const queries = [companyAISettingsQuery, getFeatureFlagQuery];

	return dataLoader([[currentUserQuery], queries]);
};

/** Page content */
const AIReviewSummarizationSettings = () => {
	const { mutateAsync: updateAiSettings, isPending } = useUpdateAiSettings();
	const { data } = useAiSettings();
	// ai feature flag enabled
	const { value: aiFeatureFlagEnabled } = useFeatureFlagQuery("ff-is-ai-enabled", false);

	const toast = useToast();

	const [enabled, setEnabled] = React.useState(data?.reviewSummarizationEnabled);
	const { state, updateForm, setNewDefaultValues, resetForm, hasUnsavedChanges } = useForm({
		schema: AiSettingsSchema.shape.reviewSummarizationSettings,
		defaultValues: data?.reviewSummarizationSettings,
	});

	// toggles behave differently in that changes are saved without having to click the save button
	const handleToggle = async (val: boolean) => {
		try {
			await updateAiSettings({ reviewSummarizationEnabled: val });
			setEnabled(val);
			toast(`Review Summarization Assistant ${val === true ? "Enabled" : "Disabled"}.`, {
				type: "success",
				size: "large",
			});
		} catch {
			showError("Unable to save settings.");
		}
	};

	const discardChanges = () => {
		resetForm();
	};

	const handleSave = async () => {
		try {
			updateAiSettings({ reviewSummarizationSettings: state });
			setNewDefaultValues(state);
			toast("Your changes have been saved.", { type: "success", size: "large" });
		} catch {
			showError("Unable to save settings.");
		}
	};

	const checkBoxOptions = [
		{ value: "strengths", label: "Key Strengths and Opportunities" },
		{ value: "quotes", label: "Relevant Quotes from the Review" },
		{ value: "actions", label: "Action Items Mentioned by Reviewers" },
		{ value: "tone", label: "Overall Tone of the Review (Positive, Negative, Neutral)" },
		{ value: "opinions", label: "Differences of Opinions Between Reviewers" },
	];
	const checkBoxValues = checkBoxOptions.filter((option) => state?.[option.value]);
	const handleCheckBoxChange = (newOptions: { label: string; value: string }[]) => {
		// If the option is in newOptions, it is true, else false
		const newSettings = checkBoxOptions.reduce((acc, curr) => {
			acc[curr.value] = newOptions.some((option) => option.value === curr.value);
			return acc;
		}, {});
		updateForm({
			...(newSettings as IAIAssistantsSettings["reviewSummarizationSettings"]),
		});
	};

	if (!aiFeatureFlagEnabled) {
		return <ErrorPage code={404} />;
	}

	return (
		<Sentry.ErrorBoundary fallback={undefined}>
			<Spacer height={4} />
			<AdminContainer>
				<PageTitle title="AI Review Summarization Settings" />
				<AdminTitle>AI Review Summarization</AdminTitle>
				<Spacer height={4} />
				<Grid cols={10} gap={2}>
					{/* left side of the page */}
					<GridCol span={4}>
						<Text size="paragraph-2" weight="semi-bold" color="neutral-9">
							Review Summarization Assistant
						</Text>
						<Spacer height={1} />
						<div className={css.description}>
							<Text size="paragraph-3" weight="regular" color="neutral-9">
								The Review Summarization Assistant allows admins, managers, and contributors to get a
								summary of the forms within a review.
							</Text>
						</div>
						<Spacer height={3} />
					</GridCol>
					<GridCol span={1} />

					{/* right side of the page */}
					<GridCol span={5}>
						<Grid cols={5}>
							{/* enable review summarization assistant toggle */}
							<GridCol span={3}>
								<Text size="paragraph-2" weight="semi-bold" color="neutral-9">
									Enable Review Summarization Assistant
								</Text>
								<Spacer height={1} />
								<div className={css.settingDescription}>
									<Text size="paragraph-3" weight="regular" color="neutral-9">
										Disabling this setting will hide the Review Summarization Assistant from admins,
										managers, and contributors.
									</Text>
								</div>
							</GridCol>

							<GridCol span={2}>
								<div data-testid="enable-toggle" className={css.toggleContainer}>
									<div className={css.toggleWrapper}>
										<Toggle onChange={(val) => handleToggle(val)} value={enabled || false} />
									</div>
								</div>
							</GridCol>

							{/* Summary Content controls */}
							<GridCol span={5}>
								<Text
									className={css.textWithInfo}
									size="paragraph-2"
									weight="semi-bold"
									color="neutral-9"
								>
									Summary Content
								</Text>
								<Spacer height={1} />
								<div className={css.settingDescription}>
									<Text size="paragraph-3" weight="regular" color="neutral-9">
										Control each section of content inside your review summary, to only highlight
										content that is relevant to you and your company. These options are in addition
										to an overall summary of the review.
									</Text>
								</div>
								<Spacer height={2} />
								<Checkboxes
									options={checkBoxOptions}
									value={checkBoxValues}
									onChange={handleCheckBoxChange}
									checkboxLabelWeight="semi-bold"
									className={css.summaryContentCheckboxes}
								/>
							</GridCol>
						</Grid>
					</GridCol>
				</Grid>
				<Spacer height={5} />
				<div className={css.controlsContainer}>
					{hasUnsavedChanges ? (
						<span data-testid="discard-changes">
							<Button className={css.controlButton} isHollow={true} onClick={discardChanges}>
								Discard
							</Button>
						</span>
					) : null}

					<span data-testid="save-changes">
						<Button
							isRequesting={isPending}
							className={css.controlButton}
							color="green"
							onClick={handleSave}
							disabled={!hasUnsavedChanges}
						>
							Save Changes
						</Button>
					</span>
				</div>
			</AdminContainer>
		</Sentry.ErrorBoundary>
	);
};

export default AIReviewSummarizationSettings;
